import { Box } from '@mui/material';
import { ReactNode } from 'react';

interface PanelHeaderProps {
  /** Sentence-case panel title (e.g. "Transcripción"). May include leading media. */
  title: ReactNode;
  /** Right-aligned action icons (search, download, etc.). */
  actions?: ReactNode;
  /**
   * Bottom hairline separating the header row from the pane body.
   * Defaults to true; set false when spacing alone is enough (e.g. media under the title).
   */
  divider?: boolean;
}

/**
 * Header for a self-contained content pane: title left, actions right, optional bottom hairline.
 * Uses a Box (not Typography) so titles can include avatars or other nodes without invalid nesting.
 */
export function PanelHeader({ title, actions, divider = true }: PanelHeaderProps) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        // Hairline needs space above and below; without it, one modest gap to the body is enough.
        ...(divider
          ? {
              pb: 1.5,
              mb: 1.5,
              borderBottom: '0.5px solid',
              borderColor: 'divider',
            }
          : { mb: 1 }),
        flexShrink: 0,
      }}
    >
      <Box
        sx={{
          typography: 'subtitle2',
          fontWeight: 500,
          color: 'text.primary',
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {title}
      </Box>
      {actions && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>{actions}</Box>
      )}
    </Box>
  );
}
